import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type AvatarVariant = 'brand' | 'success' | 'warning' | 'error' | 'info';

@Component({
  selector: 'zag-avatar',
  template: `
    @if (showImage()) {
      <img class="zag-avatar__image" [src]="src()" [alt]="ariaLabel()" (error)="onImageError()" />
    } @else {
      <span class="zag-avatar__initials">{{ displayInitials() }}</span>
    }
  `,
  styleUrl: './avatar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-avatar]': 'true',
    '[class.zag-avatar--xs]': 'size() === "xs"',
    '[class.zag-avatar--sm]': 'size() === "sm"',
    '[class.zag-avatar--md]': 'size() === "md"',
    '[class.zag-avatar--lg]': 'size() === "lg"',
    '[class.zag-avatar--xl]': 'size() === "xl"',
    '[class.zag-avatar--brand]': 'variant() === "brand" && !showImage()',
    '[class.zag-avatar--success]': 'variant() === "success" && !showImage()',
    '[class.zag-avatar--warning]': 'variant() === "warning" && !showImage()',
    '[class.zag-avatar--error]': 'variant() === "error" && !showImage()',
    '[class.zag-avatar--info]': 'variant() === "info" && !showImage()',
    '[class.zag-avatar--has-image]': 'showImage()',
    '[attr.aria-label]': 'ariaLabel()',
    role: 'img',
  },
})
export class Avatar {
  /** Full name to generate initials from (e.g., "Sarah Johnson" → "SJ") */
  readonly name = input<string>('');

  /** Optional image URL or base64 data URI to display instead of initials */
  readonly src = input<string | null | undefined>(undefined);

  /** Optional explicit initials override */
  readonly initials = input<string | undefined>(undefined);

  /** Size variant */
  readonly size = input<AvatarSize>('md');

  /** Color variant */
  readonly variant = input<AvatarVariant>('brand');

  /** Track if image failed to load */
  private readonly imageError = signal(false);

  /** Whether to show the image (src is provided and no error occurred) */
  readonly showImage = computed(() => {
    const srcValue = this.src();
    return !!srcValue && !this.imageError();
  });

  /** Handle image load error - fallback to initials */
  onImageError(): void {
    this.imageError.set(true);
  }

  /** Computed initials - uses explicit initials if provided, otherwise generates from name */
  readonly displayInitials = computed(() => {
    const explicitInitials = this.initials();
    if (explicitInitials) {
      return explicitInitials.toUpperCase().slice(0, 2);
    }

    const name = this.name();
    if (!name) {
      return '';
    }

    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  });

  /** Aria label for accessibility */
  readonly ariaLabel = computed(() => {
    const name = this.name();
    return name ? `Avatar for ${name}` : 'Avatar';
  });
}
